import React, {useEffect, useState} from 'react';
import {Calendar, Plus, Trash2} from "lucide-react";
import {StaffMember} from "../../settingsR/StaffSettings";

import { format, parseISO } from "date-fns";
import {useDispatch, useSelector} from "react-redux";
import {toast} from "react-toastify";
import {
    createBlockTime,
    deleteBlockTime as deleteBlockTimeRedux,
} from "app/redux/blockTimeSlice";
import type {RootState} from "app/redux/store";
import {fromZonedTime} from "date-fns-tz";
import {shopTimeZoneFromShops, toLocalDate} from "app/utils/timezone";

interface BlockedTime {
    id: string;
    date: string;
    startTime: string;
    endTime: string;
    reason: string;
    isRecurring?: boolean;
    recurringType?: 'daily' | 'weekly' | 'monthly';
    recurringEndDate?: string | null;
    recurringDays?: string[];
}

const RECURRING_DAY_LABELS: Record<string, string> = {
    monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
};

// "Daily · until Oct 28, 2026", or - with no end date saved - "Daily · Ongoing, no end date: blocks every
// future date until deleted". A missing end date used to show the start date, so a permanent lunch
// looked like a one-day block.
export function recurrenceLabel(blocked: Pick<BlockedTime, 'recurringType' | 'recurringEndDate' | 'recurringDays'>) {
    const days = (blocked.recurringDays || []).map((d) => RECURRING_DAY_LABELS[d] || d).join(', ');
    const repeat =
        blocked.recurringType === 'daily' ? 'Daily'
            : blocked.recurringType === 'weekly' ? `Weekly${days ? ` (${days})` : ''}`
                : blocked.recurringType === 'monthly' ? 'Monthly'
                    : 'Repeats';
    return blocked.recurringEndDate
        ? `${repeat} · until ${blocked.recurringEndDate}`
        : `${repeat} · Ongoing, no end date: blocks every future date until deleted`;
}


// The date and time typed are the shop's wall clock, whatever zone the browser is in.
export function shopTimeToUtc(date: string, time: string, timeZoneId: string) {
    return fromZonedTime(`${date}T${time}:00`, timeZoneId).toISOString();
}

// Times are shown in the block's shop zone, the same clock the calendar and booking site use - not
// the viewer's: a block added from India as "11:15 PM" used to land at 10:45 AM in a California shop.
export function transformBlockedTimes(blockedTimes?: any[], shops?: any[]) {
    if (!blockedTimes) return [];
    return blockedTimes.map((bt) => {
        const timeZoneId = shopTimeZoneFromShops(shops, bt.shopId);
        const start = toLocalDate(bt.startTime, timeZoneId);
        const end = toLocalDate(bt.endTime, timeZoneId);

        const isRecurring = !!bt.blockTimeRecurrence;

        let recurringType = null;
        let recurringEndDate = null;
        let recurringDays = null;

        if (isRecurring) {
            recurringType = bt.blockTimeRecurrence.frequency.toLowerCase();
            // The end date is a calendar day saved as UTC midnight: read its date part, not the
            // viewer's local time (which shows the day before anywhere west of UTC). Null = no end.
            const endDate = bt.blockTimeRecurrence.endDate;
            recurringEndDate = endDate ? format(parseISO(String(endDate).slice(0, 10)), "MMM d, yyyy") : null;

            if (recurringType === "weekly") {
                recurringDays = bt.blockTimeRecurrence?.recurrenceBlueprint?.recurringDays || bt.blockTimeRecurrence?.recurrenceBlueprint?.recurring_days;
            }
        }

        return {
            id: bt.id,
            date: format(start, "MMM d, yyyy"),
            startTime: format(start, "h:mm a"),
            endTime: format(end, "h:mm a"),
            isRecurring,
            recurringType,
            recurringEndDate,
            recurringDays,
            reason: bt.name ?? "",
        };
    });
}

const BlockTime = (params:any) => {


    const dispatch=useDispatch()

    const {selectedEmployee}=params





    const [selectedStaff, setSelectedStaff] = useState<StaffMember | null>(selectedEmployee);
    const shops = useSelector((state: RootState) => state.shops) as any[];
    const blockTimes = transformBlockedTimes(selectedEmployee?.blockedTimes, shops)
    const weekDays = [{key: 'monday', label: 'Monday'}, {key: 'tuesday', label: 'Tuesday'}, {
        key: 'wednesday', label: 'Wednesday'
    }, {key: 'thursday', label: 'Thursday'}, {key: 'friday', label: 'Friday'}, {
        key: 'saturday', label: 'Saturday'
    }, {key: 'sunday', label: 'Sunday'}];




    const [newBlockedTime, setNewBlockedTime] = useState({
        date: '',
        startTime: '',
        endTime: '',
        reason: '',
        isRecurring: false,
        recurringType: 'weekly' as 'daily' | 'weekly' | 'monthly',
        recurringEndDate: '',
        recurringDays: [] as string[]
    });

    const addBlockedTime = async () => {
        if (!newBlockedTime.date || !newBlockedTime.startTime || !newBlockedTime.endTime) {
            return;
        }

        const { date, startTime, endTime, reason
            ,isRecurring,
            recurringType,
            recurringEndDate,
            recurringDays } = newBlockedTime;
        const employeeId = selectedEmployee.id;
        const shopId = selectedEmployee.shops?.[0]?.id;

        if (!shopId) {
            toast.error("Employee does not have any assigned shops.");
            return;
        }

        const timeZoneId = shopTimeZoneFromShops(shops, shopId);
        const startISO = shopTimeToUtc(date, startTime, timeZoneId);
        const endISO = shopTimeToUtc(date, endTime, timeZoneId);

        try {
            await dispatch(
                createBlockTime({
                    name: reason || "Break",
                    startTime: startISO,
                    endTime: endISO,
                    blockableType: "User",
                    blockableId: employeeId,
                    shopId: shopId,

                    isRecurring,
                    recurringType,
                    recurringEndDate,
                    recurringDays




                }) as any
            ).unwrap();

            toast.success("Blocked time added");

            // Reset UI
            setNewBlockedTime({
                date: "",
                startTime: "",
                endTime: "",
                reason: "",
                isRecurring: false,
                recurringType: "weekly",
                recurringEndDate: "",
                recurringDays: [],
            });
        } catch (err:any) {
            toast.error(err.data.message??"Failed to add blocked time");
            console.error(err);
        }
    };

    const removeBlockedTime = async (id: number) => {
        try {
            await dispatch(deleteBlockTimeRedux(id) as any)

            toast.success("Blocked time deleted");

            // params.getSchedulesOfEmployees({
            //     currentDate: new Date(),
            // });

        } catch (err) {
            toast.error("Failed to delete blocked time");
            console.log(err);
        }
    };


    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Blocked Times</h3>

                {/* Add Blocked Time Form */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <h4 className="font-medium text-gray-900 mb-3">Add Blocked Time</h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <label
                                className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                            <input
                                type="date"
                                value={newBlockedTime.date}
                                onChange={(e) => setNewBlockedTime(prev => ({
                                    ...prev, date: e.target.value
                                }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Start
                                Time</label>
                            <input

                                type="time"
                                value={newBlockedTime.startTime}
                                onChange={(e) => setNewBlockedTime(prev => ({
                                    ...prev, startTime: e.target.value
                                }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">End
                                Time</label>
                            <input
                                type="time"
                                value={newBlockedTime.endTime}
                                onChange={(e) => setNewBlockedTime(prev => ({
                                    ...prev, endTime: e.target.value
                                }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label
                                className="block text-sm font-medium text-gray-700 mb-2">Reason</label>
                            <input
                                type="text"
                                value={newBlockedTime.reason}
                                onChange={(e) => setNewBlockedTime(prev => ({
                                    ...prev, reason: e.target.value
                                }))}
                                placeholder="e.g., Lunch, Training"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    {/* Recurring Options */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex items-center mb-4">
                            <input
                                type="checkbox"
                                id="recurring"
                                checked={newBlockedTime.isRecurring}
                                onChange={(e) => setNewBlockedTime(prev => ({
                                    ...prev, isRecurring: e.target.checked
                                }))}
                                className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <label htmlFor="recurring"
                                   className="text-sm font-medium text-gray-700">
                                Make this recurring
                            </label>
                        </div>

                        {newBlockedTime.isRecurring && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label
                                        className="block text-sm font-medium text-gray-700 mb-2">Repeat</label>
                                    <select
                                        value={newBlockedTime.recurringType}
                                        onChange={(e) => setNewBlockedTime(prev => ({
                                            ...prev,
                                            recurringType: e.target.value as 'daily' | 'weekly' | 'monthly',
                                            recurringDays: []
                                        }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="daily">Daily</option>
                                        <option value="weekly">Weekly</option>
                                        <option value="monthly">Monthly</option>
                                    </select>
                                </div>
                                <div>
                                    <label
                                        className="block text-sm font-medium text-gray-700 mb-2">End
                                        Date</label>
                                    <input
                                        type="date"
                                        value={newBlockedTime.recurringEndDate}
                                        onChange={(e) => setNewBlockedTime(prev => ({
                                            ...prev, recurringEndDate: e.target.value
                                        }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">Leave empty to repeat on every future date until you delete it.</p>
                                </div>
                                {newBlockedTime.recurringType === 'weekly' && (<div>
                                    <label
                                        className="block text-sm font-medium text-gray-700 mb-2">Days</label>
                                    <div className="flex flex-wrap gap-2">
                                        {weekDays.map((day) => (<label key={day.key}
                                                                       className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={newBlockedTime.recurringDays.includes(day.key)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setNewBlockedTime(prev => ({
                                                            ...prev,
                                                            recurringDays: [...prev.recurringDays, day.key]
                                                        }));
                                                    } else {
                                                        setNewBlockedTime(prev => ({
                                                            ...prev,
                                                            recurringDays: prev.recurringDays.filter(d => d !== day.key)
                                                        }));
                                                    }
                                                }}
                                                className="mr-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span
                                                className="text-xs text-gray-700">{day.label.slice(0, 3)}</span>
                                        </label>))}
                                    </div>
                                </div>)}
                            </div>)}
                    </div>

                    <button
                        onClick={addBlockedTime}
                        disabled={!newBlockedTime.date || !newBlockedTime.startTime || !newBlockedTime.endTime}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                        <Plus className="w-4 h-4"/>
                        <span>Add Blocked Time</span>
                    </button>
                </div>

                {/* Blocked Times List */}
                <div className="space-y-3 py-4">
                    {blockTimes && blockTimes?.map((blocked) => (<div key={blocked.id}
                                                                                                                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center space-x-4">
                            <Calendar className="w-5 h-5 text-gray-400"/>
                            <div>
                                <div className="flex items-center space-x-2">
                                                                    <span
                                                                        className="font-medium text-gray-900">{blocked.date}</span>
                                    {blocked.isRecurring && (<span
                                        className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                    Recurring
                                  </span>)}
                                </div>
                                <div className="text-sm text-gray-500">
                                    {blocked.startTime} - {blocked.endTime}
                                </div>
                                {blocked.isRecurring && (
                                    <div className="text-xs text-gray-500 mt-1">
                                        {recurrenceLabel(blocked as BlockedTime)}
                                    </div>)}
                            </div>
                            <div
                                className="text-sm text-gray-600">{blocked.reason}</div>
                        </div>
                        <button
                            onClick={() => removeBlockedTime(blocked.id)}
                            className="p-1 text-red-400 hover:text-red-600"
                        >
                            <Trash2 className="w-4 h-4"/>
                        </button>
                    </div>))}

                    {selectedEmployee?.blockedTimes?.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                            <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300"/>
                            <p>No blocked times set</p>
                        </div>)}
                </div>
            </div>
        </div>
    );
};

export default BlockTime;
